/**
 * /auth/check-email — "We've sent a link to your inbox."
 *
 * Reached from /auth/signup (flow=signup) or /auth/forgot (flow=forgot).
 * Both callers forward the addressee email + the flow type as search
 * params so we know which Supabase method to re-fire on Resend.
 *
 * 2026-05-20 (B-01 / B-02 closure): signup is now password-based; the
 * resend path uses supabase.auth.resend({ type: 'signup', email }) which
 * re-sends the confirmation email for the previously-created account.
 *
 * 2026-08-14: resend now rebuilds the redirect with `buildSignupRedirectUrl`,
 * the same helper signup uses, so the `?firm=` slug rides the resent link too.
 * It used to send a bare `<origin>/auth/callback`, which detached a branded
 * client from their firm whenever they opened the resent link on another
 * device — `?firm=` is the only carrier there, localStorage being empty.
 *
 * Resend behaviour:
 *   - flow=signup  → supabase.auth.resend({ type: 'signup', email })
 *   - flow=forgot  → supabase.auth.resetPasswordForEmail(email, ...)
 *   - flow missing → resend disabled (we don't know what to send;
 *                    surfaced as an inline note rather than guessing).
 *
 * Cooldown stays — 60s lockout after each click, regardless of outcome,
 * because Supabase rate-limits the underlying mailer aggressively and a
 * spinning button does not help the user.
 *
 * Falls back to the current Supabase session email if the search param
 * is missing (deep link / refresh) so we never lie about which inbox.
 */

import { useState, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { BackToSignIn } from '../../components/auth/back-to-sign-in';
import { supabase } from '../../lib/supabase';
import { buildSignupRedirectUrl, readPendingFirmSlug } from '../../lib/firm-self-signup';
import {
  stashPendingAuthEmail,
  readPendingAuthEmail,
  scrubEmailFromUrl,
} from '../../lib/pending-auth-email';

type CheckEmailFlow = 'signup' | 'forgot';

interface CheckEmailSearch {
  email?: string;
  flow?: CheckEmailFlow;
}

export const Route = createFileRoute('/auth/check-email')({
  component: CheckEmailPage,
  validateSearch: (search: Record<string, unknown>): CheckEmailSearch => {
    const out: CheckEmailSearch = {};
    if (typeof search['email'] === 'string') out.email = search['email'];
    if (search['flow'] === 'signup' || search['flow'] === 'forgot') {
      out.flow = search['flow'];
    }
    return out;
  },
});

const COOLDOWN_SECS = 60;

function CheckEmailPage() {
  const search = Route.useSearch();
  const [cooldown, setCooldown] = useState(0);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendOk, setResendOk] = useState(false);

  // Resolve the addressee: sessionStorage first (where signup/forgot now put
  // it — see lib/pending-auth-email.ts), then the current session's user email.
  //
  // A `?email=` in the URL means a link minted by the pre-2026-08-25 build, or
  // one someone forwarded. Adopt it ONCE into sessionStorage and then scrub it
  // out of the address bar, so an address that is already exposed stops
  // accumulating in history from here on rather than being re-published.
  const [stashedEmail] = useState<string | null>(() => {
    if (search.email) {
      stashPendingAuthEmail(search.email);
      return search.email;
    }
    return readPendingAuthEmail();
  });

  useEffect(() => {
    scrubEmailFromUrl();
  }, []);

  const addressee = stashedEmail ?? sessionEmail ?? '';
  const flow = search.flow;

  useEffect(() => {
    if (stashedEmail) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setSessionEmail(data.session?.user?.email ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [stashedEmail]);

  async function handleResend() {
    if (resending || cooldown > 0) return;
    if (!addressee || !flow) return;
    setResending(true);
    setResendError(null);
    setResendOk(false);

    try {
      if (flow === 'signup') {
        // Rebuild the SAME redirect signup used, slug and all. `?firm=` is the
        // only carrier of the firm association across a cross-device confirm
        // (localStorage is empty on the phone the link gets opened on), so a
        // bare origin here silently detaches a branded client from their firm.
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email: addressee,
          options: {
            emailRedirectTo: buildSignupRedirectUrl(
              window.location.origin,
              readPendingFirmSlug(new URLSearchParams(window.location.search)),
            ),
          },
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(addressee, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        });
        if (error) throw error;
      }
      setResendOk(true);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Could not resend.';
      setResendError(message);
    } finally {
      setResending(false);
      // Cooldown starts regardless of outcome — Supabase rate-limits the
      // mailer endpoint and we don't want a retry-loop pummelling it.
      setCooldown(COOLDOWN_SECS);
    }
  }

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const canResend = !!flow && !!addressee && cooldown === 0 && !resending;

  return (
    <AuthLayout
      eyebrow="Check your inbox"
      heading={flow === 'signup' ? 'Check your inbox.' : 'Link sent.'}
      sub={
        flow === 'forgot'
          ? 'Click the password-reset link in your email. The link expires after 15 minutes.'
          : // ── 2026-08-25: the hedge comes off the SIGNUP path ──────────────
            //
            // The 08-23 wording ("If this address is new to Elreign…") was added
            // so we never promise an email that Supabase deliberately will not
            // send for an address that already exists. Paul, walking prod on
            // 08-25: "Please remove that."
            //
            // It comes off safely because the existence case NEVER REACHES THIS
            // SCREEN. /auth/signup checks `identities.length === 0` and returns
            // early with "An account with that email already exists…" — an
            // outright disclosure, upstream of here. So the conditional bought
            // no enumeration protection on this path; it only made a true
            // sentence sound unsure. (The FORGOT path above keeps its
            // enumeration-safe wording, where the protection is real.)
            //
            // If signup's early return is ever removed, this sentence has to go
            // back to being conditional.
            'A confirmation link is on its way. Click it to finish creating your account. The link expires after 15 minutes.'
      }
    >
      {/* Brass envelope icon.
          Waldo 2026-08-05 — "just keep it one colour": the accent-300 outline
          around the accent-100 fill made the badge read as two brass tones
          stacked. Flat fill only; the glyph carries the contrast. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 28,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 20,
            background: 'var(--accent-100)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--accent-600)',
          }}
        >
          <Mail size={32} />
        </div>
      </div>

      {/* Addressee */}
      {addressee !== '' && (
        <div
          style={{
            background: 'var(--surface-2)',
            borderRadius: 9,
            padding: '12px 16px',
            marginBottom: 20,
            fontSize: 14,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
          }}
        >
          {/* Waldo 2026-08-05: the address used to render in the mono face at
              13.5px inside a 14px sans line — two typefaces and two sizes in one
              sentence. It now inherits the line's face and size and is
              distinguished by ink + weight alone. */}
          Sent to{' '}
          <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{addressee}</span>
        </div>
      )}

      {/* Open inbox button */}
      {/* 2026-05-20 (L-10 / F02-A-047): static info line replaces the
          previous no-op "Open your inbox" button — clicking did nothing
          before, which read as a broken primary CTA. */}
      <div
        style={{
          padding: '12px 16px',
          borderRadius: 9,
          background: 'var(--bg)',
          border: '1px solid var(--hairline)',
          marginBottom: 16,
          fontSize: 13.5,
          color: 'var(--ink-2)',
          lineHeight: 1.5,
          textAlign: 'center',
        }}
      >
        Open your email and click the link to continue.
      </div>

      {/* An escape hatch, no longer a warning.
          Until 2026-08-25 this read "Already have an account? Nothing will
          arrive — sign in instead", because an existing address got no email
          and nothing on the page admitted it. /auth/signup now catches that
          case upstream and never routes here, so the claim would be wrong on
          every path a real user takes. The link stays (a deep link to this
          screen is still possible, and someone who mistyped still wants a way
          out); the assertion goes. */}
      {flow === 'signup' && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 9,
            background: 'var(--surface-2)',
            marginBottom: 16,
            fontSize: 13.5,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
            textAlign: 'center',
          }}
        >
          Already have an account?{' '}
          <Link
            to="/auth/login"
            style={{ color: 'var(--brand-primary, var(--primary-600))', fontWeight: 500 }}
          >
            sign in instead
          </Link>
          .
        </div>
      )}

      {/* Resend feedback */}
      {resendError !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            color: '#991B1B',
            fontSize: 13,
            lineHeight: 1.45,
            marginBottom: 12,
          }}
        >
          {resendError}
        </div>
      )}
      {resendOk && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(20, 110, 80, 0.08)',
            border: '1px solid rgba(20, 110, 80, 0.3)',
            color: '#0f5c43',
            fontSize: 13,
            lineHeight: 1.45,
            marginBottom: 12,
          }}
        >
          {/* SGN-04, ruling 2026-09-03: "drop it, one plain sentence."
              The hedge ("If this address is new to Elreign…") came off the
              HEADING on 25 August for a reason set out in the `sub` prop above:
              the address-already-exists case never reaches this screen, because
              /auth/signup returns early and says so outright. The same reasoning
              applies to this confirmation, which kept the conditional wording
              and so kept sounding unsure about something we know. One sentence,
              the same one the reset path uses.
              If signup's early return is ever removed, BOTH strings go back to
              being conditional together. */}
          Link resent. Check your inbox.
        </div>
      )}

      {/* Resend with cooldown */}
      <div style={{ textAlign: 'center', fontSize: 13.5, color: 'var(--ink-3)' }}>
        Did not receive it?{' '}
        {/* `tabular-nums` (not the mono face) keeps the ticking countdown from
            re-flowing the line each second — same job, one fewer typeface. */}
        {cooldown > 0 ? (
          <span style={{ color: 'var(--ink-4)', fontVariantNumeric: 'tabular-nums' }}>
            Resend in {cooldown}s
          </span>
        ) : flow ? (
          <button
            type="button"
            onClick={() => void handleResend()}
            disabled={!canResend}
            style={{
              background: 'none',
              border: 'none',
              color: canResend ? 'var(--primary-600)' : 'var(--ink-4)',
              fontFamily: 'var(--sans)',
              fontSize: 13.5,
              fontWeight: 500,
              cursor: canResend ? 'pointer' : 'not-allowed',
              padding: 0,
            }}
          >
            {resending ? 'Resending…' : 'Resend'}
          </button>
        ) : (
          <span style={{ color: 'var(--ink-4)' }}>
            Resend unavailable (open this page from the sign-in flow).
          </span>
        )}
      </div>

      <p
        style={{
          marginTop: 20,
          fontSize: 12.5,
          color: 'var(--ink-4)',
          lineHeight: 1.55,
          textAlign: 'center',
        }}
      >
        Check your spam folder if the email does not arrive. The link is single-use.
      </p>

      {/* DESIGN GATE B2 (grader round 11, desk pass 5). After "Send reset link"
          this screen offered Resend and nothing else, so somebody who had
          opened the email on another device had no way back to sign in here.
          Not on the signup flow: that one already has its own "sign in
          instead" line above, and two links saying the same thing is noise. */}
      {flow !== 'signup' && (
        <BackToSignIn prompt={flow === 'forgot' ? 'Remembered your password?' : undefined} />
      )}
    </AuthLayout>
  );
}
